import PropTypes from 'prop-types';
import React from 'react';
import classNames from 'classnames';
import { DisclosureArrow } from 'components/DisclosureArrow/DisclosureArrow';
import { LocalizableMessage, LocalizableMessageShape } from 'components/LocalizableMessage/LocalizableMessage';
import { isFilterable } from 'helpers/filters';

/* eslint-disable react/no-multi-comp */
export class FilterTreeChild extends React.Component {
  shouldComponentUpdate(nextProps) {
    const { selected, item } = this.props;
    return selected !== nextProps.selected || item.id !== nextProps.item.id;
  }

  handleItemClicked = () => {
    const { toggleItemId, item } = this.props;
    const filterId = item.parent_id;
    toggleItemId(item.id, filterId);
  };

  render() {
    const { baseClassName, item, selected } = this.props;

    if (!item) {
      return false;
    }

    // NOTE: When this is migrated to CSS modules, make sure to add a data-qa
    // attribute for the item name and selected status.
    return (
      isFilterable(item) && (
        <li>
          <div
            className={classNames(`${baseClassName}__option`, {
              isSelected: selected,
            })}
            onClick={this.handleItemClicked}>
            <span className={`${baseClassName}__option__checkbox`} />
            <LocalizableMessage message={item.name} tagName="a" />
          </div>
        </li>
      )
    );
  }
}

FilterTreeChild.propTypes = {
  baseClassName: PropTypes.string.isRequired,
  item: PropTypes.shape({
    id: PropTypes.any.isRequired,
    filterable: PropTypes.bool,
    name: LocalizableMessageShape.isRequired,
  }),
  toggleItemId: PropTypes.func.isRequired,
  selected: PropTypes.bool,
};

export class FilterTreeParent extends React.Component {
  handleItemClicked = event => {
    const { toggleItemId, item } = this.props;
    const filterId = item.parent_id;

    if (event) {
      event.stopPropagation();
    }
    toggleItemId(item.id, filterId);
  };

  handleCollapseToggleClicked = event => {
    const { toggleParentCollapse, item } = this.props;

    if (event) {
      event.stopPropagation();
    }
    toggleParentCollapse(item.id);
  };

  render() {
    const { baseClassName, item, expanded, partiallySelected, selected } = this.props;

    if (!item) {
      return false;
    }

    const { name, id } = item;

    return (
      isFilterable(item) && (
        <li>
          <div
            className={classNames(`${baseClassName}__option`, {
              [`${baseClassName}__option--partial`]: partiallySelected,
              isSelected: selected,
              [`${baseClassName}__option--opened`]: expanded,
            })}
            onClick={this.handleItemClicked}>
            <span className={`${baseClassName}__option__checkbox`} />
            <LocalizableMessage message={name} tagName="a" />
            <DisclosureArrow
              className={`${baseClassName}__arrow`}
              open={expanded}
              onClick={this.handleCollapseToggleClicked}
            />
          </div>
          {expanded && <FilterTree {...this.props} root={id} />}
        </li>
      )
    );
  }
}

FilterTreeParent.propTypes = {
  ...FilterTreeChild.propTypes,
  item: {
    ...FilterTreeChild.propTypes.item,
    parent_id: PropTypes.any,
  },
  partiallySelected: PropTypes.bool,
  expanded: PropTypes.bool,
  toggleParentCollapse: PropTypes.func.isRequired,
};

export const FilterTree = props => {
  const {
    baseClassName,
    root,
    itemsById = {},
    itemIdSelected = {},
    parentIdPartialSelected = {},
    parentIdExpanded = {},
    parentedItemIds = {},
    toggleItemId,
  } = props;

  return (
    <ul className={baseClassName || 'filters'}>
      {parentedItemIds[root || 'root'].map(rootItemId => {
        const item = itemsById[rootItemId];
        const isParentFilter = parentedItemIds[rootItemId];

        if (isParentFilter) {
          return (
            <FilterTreeParent
              {...props}
              baseClassName={baseClassName || 'filters'}
              expanded={parentIdExpanded[rootItemId]}
              item={item}
              key={rootItemId}
              partiallySelected={parentIdPartialSelected[rootItemId]}
              selected={itemIdSelected[rootItemId]}
            />
          );
        }

        item.id = rootItemId;
        return (
          <FilterTreeChild
            baseClassName={baseClassName || 'filters'}
            item={item}
            key={rootItemId}
            selected={itemIdSelected[rootItemId]}
            toggleItemId={toggleItemId}
          />
        );
      })}
    </ul>
  );
};

FilterTree.propTypes = {
  baseClassName: PropTypes.string,
  itemIdSelected: PropTypes.object.isRequired, // eslint-disable-line react/no-unused-prop-types
  itemsById: PropTypes.object.isRequired,
  parentedItemIds: PropTypes.object.isRequired,
  parentIdExpanded: PropTypes.object.isRequired,
  parentIdPartialSelected: PropTypes.object.isRequired,
  root: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  toggleItemId: PropTypes.func.isRequired,
};
